<template>
  <div class="flex flex-col gap-6">
    <!-- KPI cards -->
    <div class="grid grid-cols-2 gap-4 lg:grid-cols-4">
      <div class="squircle-lg flex flex-col gap-1 rounded border p-4">
        <p class="text-xs font-medium text-gray-500 capitalize-first">
          {{ $t('global.total_items_sold') }}
        </p>
        <p class="text-2xl font-bold">
          {{ formatNumber(statistics.kpis.total_sold) }}
        </p>
        <p
          v-if="statistics.kpis.total_sold_evolution !== null"
          :class="evolutionClass(statistics.kpis.total_sold_evolution)"
          class="text-xs font-semibold"
        >
          {{ evolutionLabel(statistics.kpis.total_sold_evolution) }}
        </p>
      </div>

      <div class="squircle-lg flex flex-col gap-1 rounded border p-4">
        <p class="text-xs font-medium text-gray-500 capitalize-first">
          {{ $t('global.sold_types_count') }}
        </p>
        <p class="text-2xl font-bold">
          {{ statistics.kpis.distinct_types }}
        </p>
        <p class="text-xs text-gray-500">
          {{ $t('global.out_of_n_types', { n: statistics.kpis.total_types }) }}
        </p>
      </div>

      <div class="squircle-lg flex flex-col gap-1 rounded border p-4">
        <p class="text-xs font-medium text-gray-500 capitalize-first">
          {{ $t('global.best_selling_item') }}
        </p>
        <p class="truncate text-lg font-bold leading-tight">
          {{ statistics.kpis.best_item?.name ?? '—' }}
        </p>
        <p class="text-xs text-gray-500">
          {{ statistics.kpis.best_item ? $t('global.n_units', { n: formatNumber(statistics.kpis.best_item.qty) }) : '' }}
        </p>
      </div>

      <div class="squircle-lg flex flex-col gap-1 rounded border p-4">
        <p class="text-xs font-medium text-gray-500 capitalize-first">
          {{ $t('global.main_category') }}
        </p>
        <p class="truncate text-lg font-bold leading-tight">
          {{ statistics.kpis.main_category?.name ?? '—' }}
        </p>
        <p class="text-xs text-gray-500">
          {{ statistics.kpis.main_category ? statistics.kpis.main_category.ca_percent + $t('global.ca_percent') : '' }}
        </p>
      </div>
    </div>

    <!-- Types breakdown table -->
    <div v-if="!kpiOnly" id="types-breakdown" class="flex flex-col gap-2">
      <div class="flex items-center justify-between gap-3">
        <h3 class="text-base font-semibold capitalize-first">
          {{ $t('attributes.sold_types') }}
        </h3>
        <input
          v-model="searchTypes"
          type="search"
          :placeholder="$t('global.search')"
          class="w-56 rounded border border-gray-200 px-3 py-1.5 text-sm focus:border-blue-400 focus:outline-none"
        >
      </div>
      <div class="overflow-hidden rounded border">
        <table class="w-full text-sm">
          <thead class="bg-gray-50 text-xs font-semibold text-gray-500 uppercase">
            <tr>
              <th
                class="cursor-pointer px-4 py-2 text-left hover:text-gray-700"
                @click="toggleSort('name')"
              >
                {{ $t('attributes.type') }}
                <SortIndicator :active="sortBy === 'name'" :dir="sortDir" />
              </th>
              <th
                class="cursor-pointer px-4 py-2 text-right hover:text-gray-700"
                @click="toggleSort('qty')"
              >
                {{ $t('attributes.quantity') }}
                <SortIndicator :active="sortBy === 'qty'" :dir="sortDir" />
              </th>
              <th
                class="cursor-pointer px-4 py-2 text-right hover:text-gray-700"
                @click="toggleSort('ca_htva')"
              >
                {{ $t('global.ca_htva') }}
                <SortIndicator :active="sortBy === 'ca_htva'" :dir="sortDir" />
              </th>
              <th
                class="cursor-pointer px-4 py-2 text-right hover:text-gray-700"
                @click="toggleSort('percent')"
              >
                %
                <SortIndicator :active="sortBy === 'percent'" :dir="sortDir" />
              </th>
              <th class="px-4 py-2 text-left">
                {{ $t('global.distribution') }}
              </th>
              <th
                class="cursor-pointer px-4 py-2 text-right hover:text-gray-700"
                @click="toggleSort('evolution')"
              >
                {{ $t('global.evolution') }}
                <SortIndicator :active="sortBy === 'evolution'" :dir="sortDir" />
              </th>
            </tr>
          </thead>
          <tbody class="divide-y">
            <tr v-if="loadingSort" class="text-center">
              <td colspan="6" class="px-4 py-6 text-gray-400">
                <span>{{ $t('global.loading') }}</span>
              </td>
            </tr>
            <tr v-else-if="filteredTypesBreakdown.length === 0" class="text-center">
              <td colspan="6" class="px-4 py-6 text-gray-400">
                <span>{{ $t('global.no_result') }}</span>
              </td>
            </tr>
            <tr
              v-for="row in filteredTypesBreakdown"
              v-else
              :key="row.name"
              class="hover:bg-gray-50"
            >
              <td class="px-4 py-2 font-medium">{{ row.name }}</td>
              <td class="px-4 py-2 text-right tabular-nums">
                {{ formatNumber(row.qty) }}
              </td>
              <td class="px-4 py-2 text-right tabular-nums font-medium">
                {{ formatCurrency(row.ca_htva) }}
              </td>
              <td class="px-4 py-2 text-right tabular-nums text-gray-500">
                {{ row.percent }}%
              </td>
              <td class="px-4 py-2">
                <div class="h-2 w-full rounded-full bg-gray-100">
                  <div
                    class="h-2 rounded-full bg-blue-500"
                    :style="{ width: `${row.percent}%` }"
                  />
                </div>
              </td>
              <td class="px-4 py-2 text-right">
                <span
                  v-if="row.evolution !== null"
                  :class="evolutionClass(row.evolution)"
                  class="text-xs font-semibold"
                >
                  {{ evolutionLabel(row.evolution) }}
                </span>
                <span v-else class="text-xs text-gray-400">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Categories accordion -->
    <div v-if="!kpiOnly" class="flex flex-col gap-2">
      <div class="flex items-center justify-between gap-3">
        <h3 class="text-base font-semibold capitalize-first">
          {{ $t('global.top_items_by_category') }}
        </h3>
        <div class="flex items-center gap-2">
          <select
            v-model="categorySortBy"
            class="rounded border border-gray-200 px-2 py-1.5 text-sm focus:border-blue-400 focus:outline-none"
            :title="$t('global.sort')"
          >
            <option value="qty">{{ $t('global.items') }}</option>
            <option value="ca_htva">{{ $t('global.ca_htva') }}</option>
            <option value="evolution">{{ $t('global.evolution') }}</option>
          </select>
          <button
            type="button"
            class="rounded border border-gray-200 px-2 py-1.5 text-sm text-gray-500 hover:text-gray-700"
            :title="$t('global.sort')"
            @click="toggleCategorySortDir"
          >
            {{ categorySortDir === 'asc' ? '↑' : '↓' }}
          </button>
          <input
            v-model="searchCategories"
            type="search"
            :placeholder="$t('global.search_for_an_item')"
            class="w-56 rounded border border-gray-200 px-3 py-1.5 text-sm focus:border-blue-400 focus:outline-none"
          >
        </div>
      </div>
      <div v-if="filteredCategories.length === 0" class="rounded border px-4 py-6 text-center text-sm text-gray-400">
        {{ $t('global.no_result') }}
      </div>
      <div v-else class="flex flex-col divide-y rounded border overflow-hidden">
        <div
          v-for="category in filteredCategories"
          :key="category.name"
          class="flex flex-col"
        >
          <button
            class="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-gray-50"
            @click="toggleCategory(category.name)"
          >
            <div class="flex items-center gap-2">
              <FontAwesomeIcon
                :icon="['fal', isCategoryOpen(category) ? 'chevron-down' : 'chevron-right']"
                class="w-3 text-gray-400"
              />
              <span class="font-medium">{{ category.name }}</span>
              <span class="text-sm font-normal text-gray-400">({{ formatNumber(category.distinct_items) }})</span>
            </div>
            <div class="flex shrink-0 items-center gap-3 text-sm text-gray-500">
              <span>{{ formatNumber(category.total_items) }} {{ $t('global.items') }}</span>
              <span class="font-semibold text-gray-700">{{ formatCurrency(category.ca_htva) }}</span>
              <span>{{ category.ca_percent }}{{ $t('global.ca_percent') }}</span>
              <span
                v-if="category.evolution !== null"
                :class="evolutionClass(category.evolution)"
                class="w-12 text-right text-xs font-semibold"
              >
                {{ evolutionLabel(category.evolution) }}
              </span>
              <span v-else class="w-12 text-right text-xs text-gray-300">—</span>
            </div>
          </button>

          <div v-if="isCategoryOpen(category)" class="bg-gray-50 px-10 pb-3 pt-1">
            <ul class="flex flex-col gap-1">
              <li
                v-for="item in visibleItems(category)"
                :key="item.name"
                class="flex items-center justify-between gap-3 text-sm"
              >
                <span class="text-gray-700">{{ item.name }}</span>
                <span class="flex shrink-0 items-center gap-3 tabular-nums">
                  <span class="text-gray-500">{{ formatNumber(item.qty) }} ({{ item.item_percent }}%)</span>
                  <span class="w-24 text-right font-medium text-gray-700">{{ formatCurrency(item.ca_htva) }}</span>
                  <span
                    v-if="item.evolution !== null"
                    :class="evolutionClass(item.evolution)"
                    class="w-12 text-right text-xs font-semibold"
                  >
                    {{ evolutionLabel(item.evolution) }}
                  </span>
                  <span v-else class="w-12 text-right text-xs text-gray-300">—</span>
                </span>
              </li>
            </ul>
            <button
              v-if="hiddenCount(category) > 0"
              class="mt-2 text-xs text-blue-600 hover:underline"
              @click.stop="revealMore(category)"
            >
              {{ $t('global.load_n_more_items', { n: nextChunk(category) }) }}
            </button>
            <p
              v-else-if="category.other_count > 0 && !isSearchingCategories"
              class="mt-2 text-xs text-gray-400"
            >
              {{ $t('global.see_n_other_items', { n: category.other_count }) }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, h } from 'vue'
import { mapActions, mapGetters } from 'vuex'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

const DEFAULT_VISIBLE_ITEMS = 10
const ITEMS_REVEAL_CHUNK = 30

// Render function (not template string): runtime template compiler unavailable in Vite build
const SortIndicator = defineComponent({
  name: 'SortIndicator',
  props: {
    active: { type: Boolean, default: false },
    dir: { type: String, default: 'desc' }
  },
  render() {
    return h(
      'span',
      { class: ['ml-1 text-gray-400', { 'text-gray-700': this.active }] },
      this.active ? (this.dir === 'asc' ? '↑' : '↓') : '↕'
    )
  }
})

export default defineComponent({
  name: 'AnalyticsArticleStats',
  components: { FontAwesomeIcon, SortIndicator },
  props: {
    statistics: {
      type: Object,
      required: true
    },
    config: {
      type: Object,
      required: true
    },
    startDate: {
      type: String,
      default: null
    },
    endDate: {
      type: String,
      default: null
    },
    compareStart: {
      type: String,
      default: null
    },
    compareEnd: {
      type: String,
      default: null
    },
    kpiOnly: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:statistics'],
  data() {
    return {
      sortBy: 'qty',
      sortDir: 'desc',
      loadingSort: false,
      openCategories: new Set(),
      typesBreakdown: [],
      visibleCounts: {},
      searchTypes: '',
      searchCategories: '',
      categorySortBy: 'qty',
      categorySortDir: 'desc'
    }
  },
  computed: {
    ...mapGetters({
      currency: 'metadata/getDefaultCurrencyMetadata'
    }),
    filteredTypesBreakdown() {
      const query = this.$h.normalizeSearch(this.searchTypes)
      if (!query) return this.typesBreakdown
      return this.typesBreakdown.filter((row) => this.$h.normalizeSearch(row.name).includes(query))
    },
    isSearchingCategories() {
      return this.$h.normalizeSearch(this.searchCategories).length > 0
    },
    filteredCategories() {
      const categories = this.statistics.categories_breakdown ?? []
      const query = this.$h.normalizeSearch(this.searchCategories)
      if (!query) return categories

      return categories
        .map((category) => {
          if (this.$h.normalizeSearch(category.name).includes(query)) {
            return category
          }
          const matchingItems = category.top_items.filter((item) =>
            this.$h.normalizeSearch(item.name).includes(query)
          )
          if (matchingItems.length === 0) return null
          return { ...category, top_items: matchingItems }
        })
        .filter(Boolean)
    }
  },
  watch: {
    statistics: {
      immediate: true,
      handler(newVal) {
        this.typesBreakdown = newVal?.types_breakdown ?? []
        this.visibleCounts = {}
      }
    }
  },
  methods: {
    ...mapActions({
      processGetInvoiceItemStatistics: 'analytics/getInvoiceItemStatistics'
    }),
    formatNumber(value) {
      return new Intl.NumberFormat(this.$i18n.locale.replace('_', '-')).format(value)
    },
    formatCurrency(value) {
      return new Intl.NumberFormat(this.$i18n.locale.replace('_', '-'), {
        style: 'currency',
        currency: this.currency ?? 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value ?? 0)
    },
    evolutionClass(value) {
      if (value > 0) return 'text-green-600'
      if (value < 0) return 'text-red-500'
      return 'text-gray-500'
    },
    evolutionLabel(value) {
      // Item-name matching can't track renames year-over-year, which yields absurd spikes; clamp the display
      if (value > 999) return '+999%'
      if (value > 0) return `+${value}%`
      if (value < 0) return `${value}%`
      return '0%'
    },
    toggleCategory(name) {
      const updated = new Set(this.openCategories)
      if (updated.has(name)) {
        updated.delete(name)
      } else {
        updated.add(name)
      }
      this.openCategories = updated
    },
    isCategoryOpen(category) {
      return this.isSearchingCategories || this.openCategories.has(category.name)
    },
    visibleCount(name) {
      return this.visibleCounts[name] ?? DEFAULT_VISIBLE_ITEMS
    },
    sortItems(items) {
      const key = this.categorySortBy
      const direction = this.categorySortDir === 'asc' ? 1 : -1
      return [...items].sort((first, second) => {
        const firstValue = first[key]
        const secondValue = second[key]
        // evolution can be null (no comparable base) → always push to the bottom
        if (key === 'evolution') {
          if (firstValue === null && secondValue === null) return 0
          if (firstValue === null) return 1
          if (secondValue === null) return -1
        }
        return (firstValue - secondValue) * direction
      })
    },
    toggleCategorySortDir() {
      this.categorySortDir = this.categorySortDir === 'asc' ? 'desc' : 'asc'
    },
    visibleItems(category) {
      const sorted = this.sortItems(category.top_items)
      // While searching, show every match (no progressive reveal)
      if (this.isSearchingCategories) return sorted
      return sorted.slice(0, this.visibleCount(category.name))
    },
    hiddenCount(category) {
      if (this.isSearchingCategories) return 0
      return Math.max(0, category.top_items.length - this.visibleCount(category.name))
    },
    nextChunk(category) {
      return Math.min(ITEMS_REVEAL_CHUNK, this.hiddenCount(category))
    },
    revealMore(category) {
      this.visibleCounts = {
        ...this.visibleCounts,
        [category.name]: this.visibleCount(category.name) + ITEMS_REVEAL_CHUNK
      }
    },
    async toggleSort(column) {
      if (this.sortBy === column) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortBy = column
        this.sortDir = 'desc'
      }

      this.loadingSort = true
      try {
        const response = await this.processGetInvoiceItemStatistics({
          config: this.config,
          start_date: this.startDate,
          end_date: this.endDate,
          compare_start: this.compareStart,
          compare_end: this.compareEnd,
          sort_by: this.sortBy,
          sort_dir: this.sortDir
        })
        this.typesBreakdown = response.statistics?.types_breakdown ?? []
      } finally {
        this.loadingSort = false
      }
    }
  }
})
</script>
